import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card, Group, Stack, TextInput, Title, Table, ActionIcon, Modal, Text } from '@mantine/core';
import { IconPlus, IconTrash, IconPencil, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Customer, UpsertCustomer } from '../services/customersService';
import { listCustomers, createCustomer, updateCustomer, deleteCustomer } from '../services/customersService';

const CustomersPage: React.FC = () => {
  const translation = useTranslation();
  const t = translation?.t || ((key: string) => key);
  const [items, setItems] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<UpsertCustomer>({ company: '', street: '', zip: '', city: '', country: '', poc: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    company: '',
    street: '',
    zip: '',
    city: '',
    country: '',
    poc: ''
  });
  
  const emptyForm: UpsertCustomer = { company: '', street: '', zip: '', city: '', country: '', poc: '' };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await listCustomers();
      setItems(res.items);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Filter items based on filters
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Text filters (case-insensitive)
      if (filters.company && !item.company.toLowerCase().includes(filters.company.toLowerCase())) return false;
      if (filters.street && !item.street.toLowerCase().includes(filters.street.toLowerCase())) return false;
      if (filters.zip && !item.zip.toLowerCase().includes(filters.zip.toLowerCase())) return false;
      if (filters.city && !item.city.toLowerCase().includes(filters.city.toLowerCase())) return false;
      if (filters.country && !item.country.toLowerCase().includes(filters.country.toLowerCase())) return false;
      if (filters.poc && !item.poc.toLowerCase().includes(filters.poc.toLowerCase())) return false;
      
      return true;
    });
  }, [items, filters]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      company: '',
      street: '',
      zip: '',
      city: '',
      country: '',
      poc: ''
    });
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (c: Customer) => { 
    setEditingId(c.id); 
    setForm({ 
      company: c.company || '', 
      street: c.street || '', 
      zip: c.zip || '', 
      city: c.city || '', 
      country: c.country || '', 
      poc: c.poc || '' 
    }); 
    setModalOpen(true); 
  };

  const onSubmit = async () => {
    try {
      if (editingId) {
        await updateCustomer(editingId, form);
      } else {
        await createCustomer(form);
      }
      setModalOpen(false);
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      alert(msg || 'Save failed');
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Delete this customer?')) return;
    try { await deleteCustomer(id); await load(); } catch (e: unknown) { const msg = e instanceof Error ? e.message : String(e); alert(msg || 'Delete failed'); }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>{t('admin.customers.title') || 'Customers'}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>{t('common.new') || 'New'}</Button>
      </Group>

      {/* Filters Section */}
      <Card withBorder>
        <Title order={4} mb="md">{t('common.filters') || 'Filters'}</Title>
        <Stack gap="sm">
          {/* First row of filters */}
          <Group grow>
            <TextInput
              label={t('admin.customers.company') || 'Company'}
              placeholder="Filter by company..."
              value={filters.company}
              onChange={(e) => setFilters(f => ({ ...f, company: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <TextInput
              label={t('admin.customers.address') || 'Address'}
              placeholder="Filter by address..."
              value={filters.street}
              onChange={(e) => setFilters(f => ({ ...f, street: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <TextInput
              label={t('admin.customers.zip') || 'ZIP'}
              placeholder="Filter by ZIP..."
              value={filters.zip}
              onChange={(e) => setFilters(f => ({ ...f, zip: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
          </Group>
          
          {/* Second row of filters */}
          <Group grow>
            <TextInput
              label={t('admin.customers.city') || 'City'}
              placeholder="Filter by city..."
              value={filters.city}
              onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <TextInput
              label={t('admin.customers.country') || 'Country'}
              placeholder="Filter by country..."
              value={filters.country}
              onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))}
              leftSection={<IconSearch size={16} />}
            />
            <Group>
              <TextInput
                label={t('admin.customers.poc') || 'POC'}
                placeholder="Filter by POC..."
                value={filters.poc}
                onChange={(e) => setFilters(f => ({ ...f, poc: e.target.value }))}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button variant="outline" onClick={clearFilters} style={{ marginTop: '24px' }}>
                {t('common.clear') || 'Clear'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        {error && (
          <Text c="red" mb="sm">{error}</Text>
        )}
        {loading && (
          <Text c="dimmed" mb="sm">{t('common.sending') || 'Loading...'}</Text>
        )}
        {filteredItems.length === 0 ? (
          <div style={{ color: 'var(--mantine-color-dimmed)' }}>
            {items.length === 0 
              ? (t('data.table.noData') || 'Keine Daten')
              : (t('data.table.noData') || 'No customers match your filters')
            }
          </div>
        ) : (
          <Table striped withRowBorders={false} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin.customers.company') || 'Company'}</Table.Th>
                <Table.Th>{t('admin.customers.address') || 'Address'}</Table.Th>
                <Table.Th>{t('admin.customers.zip') || 'ZIP'}</Table.Th>
                <Table.Th>{t('admin.customers.city') || 'City'}</Table.Th>
                <Table.Th>{t('admin.customers.country') || 'Country'}</Table.Th>
                <Table.Th>{t('admin.customers.poc') || 'POC'}</Table.Th>
                <Table.Th style={{ width: 80 }}>{t('users.actions') || 'Actions'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{c.company}</Table.Td>
                  <Table.Td>{c.street}</Table.Td>
                  <Table.Td>{c.zip}</Table.Td>
                  <Table.Td>{c.city}</Table.Td>
                  <Table.Td>{c.country}</Table.Td>
                  <Table.Td>{c.poc}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="light" onClick={() => openEdit(c)}><IconPencil size={16} /></ActionIcon>
                      <ActionIcon color="red" variant="light" onClick={() => onDelete(c.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? (t('admin.customers.edit') || 'Edit customer') : (t('admin.customers.create') || 'Create customer')}>
        <Stack>
          <TextInput label={t('admin.customers.company') || 'Company'} placeholder="Company" value={form.company || ''} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} />
          <TextInput label={t('admin.customers.address') || 'Address'} placeholder="MyStreet 1" value={form.street || ''} onChange={(e) => setForm(f => ({ ...f, street: e.target.value }))} />
          <Group grow>
            <TextInput label={t('admin.customers.zip') || 'ZIP'} placeholder="12345" value={form.zip || ''} onChange={(e) => setForm(f => ({ ...f, zip: e.target.value }))} />
            <TextInput label={t('admin.customers.city') || 'City'} placeholder="MyCity" value={form.city || ''} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} />
          </Group>
          <Group grow>
            <TextInput label={t('admin.customers.country') || 'Country'} placeholder="Germany" value={form.country || ''} onChange={(e) => setForm(f => ({ ...f, country: e.target.value }))} />
            <TextInput label={t('admin.customers.poc') || 'POC'} placeholder="John Doe" value={form.poc || ''} onChange={(e) => setForm(f => ({ ...f, poc: e.target.value }))} />
          </Group>
          <Group justify="flex-end"><Button onClick={onSubmit}>{t('common.save') || 'Save'}</Button></Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default CustomersPage;
